import { useToast } from "@/hooks/use-toast";
import { useState } from "react";
import { useQueryClient, useMutation, useQuery } from "@tanstack/react-query";
import { PARTNER_TYPES } from "@shared/schema";
import type { Partner } from "@shared/schema";
import { formatPartnerCommission } from "@shared/fees";
import { apiRequest, parseErrorMessage } from "@/lib/queryClient";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Handshake } from "lucide-react";
import { ReclassifyResult } from "./types";
import { PartnerDetailView } from "./PartnerDetailView";

export function AllPartnersPanel() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [selectedPartnerId, setSelectedPartnerId] = useState<number | null>(null);
  const [reclassifyResult, setReclassifyResult] = useState<ReclassifyResult | null>(null);

  const partnersQuery = useQuery<Partner[]>({
    queryKey: ["/api/admin/partners", typeFilter],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (typeFilter && typeFilter !== "all") params.set("partnerType", typeFilter);
      const res = await fetch(`/api/admin/partners?${params}`, { credentials: "include" });
      return res.json();
    },
  });

  const reclassifyMutation = useMutation({
    meta: { silentSave: true },
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/partners/reclassify-brokers", {});
      return (await res.json()) as ReclassifyResult;
    },
    onSuccess: (data) => {
      setReclassifyResult(data);
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
      toast({
        title: "Reclassification complete",
        description: `${data.reclassified} reclassified, ${data.cleared} cleared.`,
      });
    },
    onError: (err: unknown) => {
      toast({
        title: "Reclassification failed",
        description: parseErrorMessage(err, "Couldn't reclassify. Please try again."),
        variant: "destructive",
      });
    },
  });

  const selectedPartner = (partnersQuery.data ?? []).find(p => p.id === selectedPartnerId) ?? null;
  const typeLabel = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

  if (selectedPartner) {
    return <PartnerDetailView partner={selectedPartner} onBack={() => setSelectedPartnerId(null)} onUpdated={() => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/partners"] });
    }} />;
  }

  const allPartners = partnersQuery.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[140px] h-8 text-xs" data-testid="select-partner-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {PARTNER_TYPES.map(t => <SelectItem key={t} value={t}>{typeLabel(t)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button
          size="sm"
          variant="outline"
          onClick={() => reclassifyMutation.mutate()}
          disabled={reclassifyMutation.isPending}
          data-testid="button-reclassify-brokers"
        >
          {reclassifyMutation.isPending ? "Reclassifying…" : "Reclassify brokers"}
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">{allPartners.length} partner{allPartners.length !== 1 ? "s" : ""}</span>
      </div>
      <Dialog open={!!reclassifyResult} onOpenChange={(o) => { if (!o) setReclassifyResult(null); }}>
        <DialogContent data-testid="dialog-reclassify-result">
          <DialogHeader>
            <DialogTitle>Broker reclassification result</DialogTitle>
            <DialogDescription>{reclassifyResult?.message}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <div className="flex gap-4">
              <div><span className="text-muted-foreground">Reclassified:</span> <span className="font-semibold" data-testid="text-reclassified-count">{reclassifyResult?.reclassified ?? 0}</span></div>
              <div><span className="text-muted-foreground">Cleared:</span> <span className="font-semibold" data-testid="text-cleared-count">{reclassifyResult?.cleared ?? 0}</span></div>
            </div>
            {reclassifyResult && reclassifyResult.updated.length > 0 ? (
              <div className="max-h-64 overflow-y-auto border rounded">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Partner</TableHead>
                      <TableHead>Company</TableHead>
                      <TableHead>From → To</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reclassifyResult.updated.map(u => (
                      <TableRow key={u.partnerId} data-testid={`row-reclassified-${u.partnerId}`}>
                        <TableCell>#{u.partnerId}</TableCell>
                        <TableCell>{u.companyName}</TableCell>
                        <TableCell><Badge variant="outline" className="text-xs">{u.from} → {u.to}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <p className="text-muted-foreground text-xs">No partners were moved to the broker type.</p>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setReclassifyResult(null)} data-testid="button-close-reclassify-result">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Card>
        {partnersQuery.isLoading ? (
          <div className="p-6 space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-10 w-full" />)}</div>
        ) : allPartners.length === 0 ? (
          <div className="p-12 text-center">
            <Handshake className="mx-auto h-10 w-10 text-muted-foreground/40 mb-3" />
            <p className="text-muted-foreground">No partners yet.</p>
            <p className="text-xs text-muted-foreground/70 mt-1">Approve applications to create partner accounts.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Commission</TableHead>
                  <TableHead>Joined</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {allPartners.map(p => (
                  <TableRow key={p.id} className="cursor-pointer hover:bg-muted/50" onClick={() => setSelectedPartnerId(p.id)} data-testid={`row-partner-${p.id}`}>
                    <TableCell className="font-medium">{p.contactName}</TableCell>
                    <TableCell>{p.companyName}</TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{typeLabel(p.partnerType)}</Badge></TableCell>
                    <TableCell className="text-muted-foreground text-sm">{p.email}</TableCell>
                    <TableCell>
                      {p.suspended ? (
                        <Badge variant="destructive" className="text-xs">Suspended</Badge>
                      ) : p.active ? (
                        <Badge variant="default" className="text-xs">Active</Badge>
                      ) : (
                        <Badge variant="secondary" className="text-xs">Inactive</Badge>
                      )}
                      {p.needsReclassification && (
                        <Badge variant="outline" className="text-xs ml-1 text-amber-600 border-amber-400">Reclassify</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {formatPartnerCommission(p.commissionType, p.commissionAmount)}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs">{p.createdAt ? new Date(p.createdAt).toLocaleDateString() : "—"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
